import { bootstrapApplication } from '@angular/platform-browser';
import { appConfig } from './app/app.config';
import { AppComponent } from './app/app.component';
import { inject, provideEnvironmentInitializer, runInInjectionContext } from '@angular/core';
import { AuthService } from "./app/auth/service/auth.service";
import { LocalStorageService } from "@coreui/angular-pro";
import { SystemService } from "@shared/service/system/system.service";
import { DashboardDataCacheService } from "@shared/service/data-cache/dashboard-data-cache.service";
import { LayoutService } from "./app/layout/service/layout.service";
import { GlobalErrorHandler } from "@shared/utils/global-error-handler";
import { CustomTitleStrategy } from "@shared/utils/custom-title-strategy";
import { TitleStrategy } from "@angular/router";
import { environment } from "./environments/environment";
import { filter, firstValueFrom } from "rxjs";
import * as Sentry from '@sentry/angular';

// Enable full debug info in dev
((Error as unknown) as { stackTraceLimit: number; }).stackTraceLimit = Infinity;


export const systemInitializerProviders = provideEnvironmentInitializer(async () => {
  const authService = inject(AuthService);
  const systemService = inject(SystemService);
  const dashboardCache = inject(DashboardDataCacheService);

  return firstValueFrom(authService.user$.pipe(filter(Boolean)))
    .then(() => systemService.initialize())
    .then(() => dashboardCache.initialize())
    .then(() => {
      // Initialize Sentry after system settings are loaded
      const sentryConfig = systemService.systemSettings?.sentry;
      if (sentryConfig?.SENTRY_DSN) {
        console.log('[Sentry] Initializing');

        Sentry.init({
          dsn: sentryConfig.SENTRY_DSN,
          environment: sentryConfig.SENTRY_ENV,
          release: sentryConfig.RELEASE,
          integrations: [
            Sentry.browserTracingIntegration(),
            Sentry.captureConsoleIntegration({
              levels: ['error', 'warn'],
            }),
          ],
          // Performance Monitoring
          tracesSampleRate: environment.PRODUCTION ? 0.1 : 1.0,
          // Session Replay
          replaysSessionSampleRate: 0.1,
          replaysOnErrorSampleRate: 1.0,
        });

        console.log('[Sentry] ✓ Initialized successfully');
      }
    });
});

bootstrapApplication(AppComponent, {
  providers: [
    ...appConfig.providers,
    SystemService,
    systemInitializerProviders,
    { provide: TitleStrategy, useClass: CustomTitleStrategy },
    AuthService,
    LayoutService,
    LocalStorageService,
  ],
}).then((appRef) => {
  const injector = appRef.injector;

  runInInjectionContext(injector, async () => {
    const errorHandler = injector.get(GlobalErrorHandler);
    window.onerror = (_, __, ___, ____, error) => {
      errorHandler.handleError(error);
    };
    window.onunhandledrejection = (event) => {
      errorHandler.handleError(event.reason || event);
    };
  });
}).catch(err => console.error(err));

document.head.innerHTML += `<link rel="icon" type="image/x-icon" href="${environment.STATIC_LOCATION}/assets/images/favicon.ico">`;
